import React, { useState, useEffect } from "react";
import RateCardLabel from "./components/RateCardLabel";
import { PermissionOnly, RootOnly } from "../../components/AdminOnly";
import { BookingStatus } from "./components/BookingStatus";
import { Button, HollowButton } from "../../components/Button";
import {
  Container,
  CardContainer,
  ColContainer,
} from "../../components/Container";
import { H1, H3, H4, HR, Byline } from "../../components/Typography";
import { Loading, LoadingError } from "../../components/LoadingFork";
import { StatusPill } from "../../components/StatusPill";
import { StripeElementsInput } from "../../components/StripeElementsInput";
import { Table, TBody, TD, TR } from "../../components/Table";
import { BayAccessSection } from "../../components/bayAccess/BayAccessSection";
import { isParklioBayAccessEnabled } from "../../lib/parklioFeature";
import { css } from "glamor";
import { gray40 } from "../../theme";
import { useQuery } from "@apollo/client";
import { withAppConfig } from "../../components/AppConfigContext";
import { withInstallation } from "../../components/InstallationContext";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogContentText from "@material-ui/core/DialogContentText";
import { deleteBookingMutation } from "./mutations";
import { getStripePaymentMethods } from "./queries";

import ResendEmail from "./ResendEmail";
import RequestRefund from "./RequestRefund";
import {
  details,
  formatCurrency,
  format24HPeriod,
} from "../../lib/rateCardUtils";
import { bookingById } from "./queries";
import drivoApollo from "../../lib/drivoApollo";
import { useHistory } from "react-router-dom";
import { fmtDate } from "../../lib/misc";
import RequestCancellation from "./RequestCancellation";
import { getBookingFocReasonLabel } from "../../lib/enums";
import api from "../../lib/api";
import authManager, {PERMISSIONS} from "../../lib/authManager";

const FLAVOUR_MAP = {
  SUCCESS: "success",
  PENDING: "warning",
  ERROR: "danger",
};

const PARKING_STATUS_FLAVOUR = {
  CLOSED: "success",
  OPEN: "warning",
  EXPIRED: "gray",
  ENTRY_MISSING: "danger",
  EXIT_MISSING: "danger",
};

export function isLocked(booking) {
  if (!booking) return false;
  const { status } = booking;
  return ["FINALISED", "ERROR"].includes(status);
}

function RowHead(props) {
  return (
    <td
      className={css({
        width: "30%",
        color: gray40,
      })}
      {...props}
    />
  );
}

export function getOhipConfirmation(pmsInformation) {
  if (!pmsInformation || pmsInformation === "{}") return "";
  try {
    const parsed = JSON.parse(pmsInformation);
    return (
      parsed.detail?.find((d) => d.elementName === "CONFIRMATION NO")
        ?.newValue ?? ""
    );
  } catch {
    return "";
  }
}

function CappedPill() {
  return (
    <StatusPill flavour="warning" size="xsmall">
      Capped
    </StatusPill>
  );
}

export function formatStatusLabel(status) {
  if (!status) return "Unknown";

  return status
    .toLowerCase()
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

export function formatParkingDuration(minutes) {
  if (minutes == null || Number.isNaN(minutes)) return "-";

  const totalMinutes = Math.max(0, Math.floor(minutes));
  const hours = Math.floor(totalMinutes / 60);
  const mins = totalMinutes % 60;

  if (hours && mins) return `${hours}h ${mins}m`;
  if (hours) return `${hours}h`;
  return `${mins}m`;
}

export function getParkingDurationMinutes(session) {
  if (!session) return null;

  if (session.status === "CLOSED" && session.parkedMinutes != null) {
    return session.parkedMinutes;
  }

  if (session.status === "OPEN" && session.entryAt) {
    return (Date.now() - new Date(session.entryAt).getTime()) / 60000;
  }

  if (session.entryAt && session.exitAt) {
    return (new Date(session.exitAt).getTime() - new Date(session.entryAt).getTime()) / 60000;
  }

  return session.parkedMinutes ?? null;
}

function ExpiryPill() {
  return (
    <StatusPill flavour="danger" size="xsmall">
      Expired
    </StatusPill>
  );
}

function BookingDetail({ match, installation, facility, appConfig }) {
  function RefundRow({
    appConfig,
    id,
    status,
    amountAud,
    createdAt,
    updatedAt,
    currency,
  }) {
    const finalisedAt = status === "PROCESSED" ? updatedAt : null;
    const acceptUrl = finalisedAt
      ? null
      : `/refunds/${id}/mark-as-processed`;

    return (
      <ChargeRow
        amountAud={amountAud}
        createdAt={createdAt}
        finalisedAt={finalisedAt}
        currency={currency}
      >
        Refund{" "}
        {acceptUrl ? (
          <RootOnly>
            <a
              href={acceptUrl}
              className={css({ "font-size": 12, color: gray40 })}
            >
              (mark as processed)
            </a>
          </RootOnly>
        ) : null}
      </ChargeRow>
    );
  }

  function ChargeRow({
    amountAud,
    error,
    createdBy,
    createdAt,
    finalisedAt,
    currency,
    children,
  }) {
    const status = error ? "ERROR" : finalisedAt ? "SUCCESS" : "PENDING";

    const dateStr = fmtDate(
      new Date(finalisedAt || createdAt).toISOString(),
      installation.timeZoneName,
      "EEE, d MMM yyyy HH:mm ZZZZ",
    );

    return (
      <TR>
        <RowHead>{children}</RowHead>
        <TD>
          {dateStr}
          {createdBy && createdBy.name ? (
            <>
              <br />
              <small style={{ color: gray40 }}>{createdBy.name}</small>
            </>
          ) : null}
          {error ? (
            <>
              <br />
              <small style={{ color: gray40 }}>{error}</small>
            </>
          ) : null}
        </TD>
        <TD>
          {formatCurrency(Number(amountAud), currency)}{" "}
          <StatusPill flavour={FLAVOUR_MAP[status]} size="xsmall">
            {status}
          </StatusPill>
        </TD>
      </TR>
    );
  }

  function EmailModal({ data, onClose }) {
    return (
        <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="email-title"
            className={css({
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              background: 'rgba(0,0,0,0.5)',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              zIndex: 1000,
            })}
        >
          <div
              className={css({
                background: 'white',
                width: '80%',
                height: '80%',
                padding: 20,
                overflow: 'auto',
                borderRadius: 8,
              })}
          >
            <button
                onClick={onClose}
                className={css({
                  float: 'right',
                  cursor: 'pointer',
                  border: 'none',
                  background: 'none',
                  fontSize: 24,
                })}
                aria-label="Close"
                type="button"
            >
              ✕
            </button>

            <h2>{data.subject}</h2>
            <p><b>To:</b> {data.to}</p>
            <p><b>From:</b> {data.from}</p>
            {data.cc && <p><b>CC:</b> {data.cc}</p>}
            <iframe srcDoc={data.html} style={{ width: '100%', height: '100%', border: 'none' }} title="Email Preview" />
          </div>
        </div>
    );
  }

  function EmailRow({ id, mailgunId, templateClass, finalisedAt }) {
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [resending, setResending] = useState(false);
    const [emailData, setEmailData] = useState(null);

    const isError = mailgunId && mailgunId.startsWith("Error:");
    const status = finalisedAt ? (isError ? "ERROR" : "SUCCESS") : "PENDING";

    const handleOpen = async () => {
      try {
        setLoading(true);

        const { data } = await api.emailPreview(id);

        setEmailData(data);
        setOpen(true);
      } catch (err) {
        console.error(err);
        alert('Failed to load email preview');
      } finally {
        setLoading(false);
      }
    };

    const handleResend = async () => {
      try {
        setResending(true);

        await api.emailResend(id);

        alert('Email queued for resend');
      } catch (err) {
        console.error(err);
        alert(err.message || 'Failed to resend email');
      } finally {
        setResending(false);
      }
    };

    return (
      <>
        <TR>
          <RowHead>
            Email{" "}
            <RootOnly>
              <button
                  onClick={handleOpen}
                  disabled={loading}
                  className={css({
                    fontSize: 12,
                    color: gray40,
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    textDecoration: 'underline',
                  })}
              >
                {loading ? 'Loading...' : '(view)'}
              </button>
            </RootOnly>
          </RowHead>

          <TD>
            {finalisedAt
              ? fmtDate(
                new Date(finalisedAt).toISOString(),
                installation.timeZoneName,
                "EEE, d MMM yyyy HH:mm ZZZZ",
              )
              : " "}
          </TD>

          <TD>
            {templateClass}{" "}
            <StatusPill flavour={FLAVOUR_MAP[status]} size="xsmall">
              {status}
            </StatusPill>
            {status === "ERROR" && (
              <PermissionOnly permissions={PERMISSIONS.BOOKINGS_MANAGE}>
                <button
                  onClick={handleResend}
                  disabled={resending}
                  className={css({
                    marginLeft: 8,
                    fontSize: 12,
                    color: '#d32f2f',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    textDecoration: 'underline',
                  })}
                >
                  {resending ? 'Resending...' : 'Resend'}
                </button>
              </PermissionOnly>
            )}
          </TD>
        </TR>
        {open && emailData && (
          <EmailModal data={emailData} onClose={() => setOpen(false)} />
        )}
      </>
    );
  }

  function EventRow({ gateType, gateDescription, pcLoggedAt }) {
    return (
      <TR>
        <RowHead>Event</RowHead>
        <TD>
          {fmtDate(
            pcLoggedAt,
            installation.timeZoneName,
            "EEE, d MMM yyyy HH:mm ZZZZ",
          )}
          <br />
          <small style={{ color: gray40 }}>{gateDescription}</small>
        </TD>
        <TD>{gateType}</TD>
      </TR>
    );
  }

  function ParkingSessionCard({ session }) {
    const status = session.status || "UNKNOWN";
    const durationMinutes = getParkingDurationMinutes(session);
    const entryDescription =
      session.entryGateDescription || session.entryGateId || "Entry gate unknown";
    const exitDescription =
      session.exitGateDescription || session.exitGateId || "Exit gate unknown";
    const statusFlavour = PARKING_STATUS_FLAVOUR[status] || "gray";

    return (
      <div
        className={css({
          border: `1px solid #EAEDEF`,
          borderRadius: 8,
          padding: 16,
          marginBottom: 12,
          background: "#FFFFFF",
        })}
      >
        <div
          className={css({
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: 16,
            marginBottom: 16,
          })}
        >
          <div>
            <strong>{session.plate || session.normalizedPlate || booking?.plate || "Unknown plate"}</strong>
          </div>
          <StatusPill flavour={statusFlavour}>
            {formatStatusLabel(status)}
          </StatusPill>
        </div>

        <div
          className={css({
            display: "grid",
            gridTemplateColumns: "1fr auto 1fr",
            gap: 16,
            alignItems: "center",
          })}
        >
          <ParkingSessionPoint
            label="Entry"
            at={session.entryAt}
            description={entryDescription}
            observationId={session.entryObservationId}
          />
          <div
            className={css({
              minWidth: 96,
              textAlign: "center",
              color: gray40,
              fontSize: 12,
            })}
          >
            <div
              className={css({
                height: 2,
                background: "#EAEDEF",
                marginBottom: 8,
              })}
            />
            <strong className={css({ color: "#333", fontSize: 14 })}>
              {formatParkingDuration(durationMinutes)}
            </strong>
            <div>{status === "OPEN" ? "current duration" : "parked"}</div>
          </div>
          <ParkingSessionPoint
            label="Exit"
            at={session.exitAt}
            description={exitDescription}
            observationId={session.exitObservationId}
          />
        </div>
      </div>
    );
  }

  function ParkingSessionPoint({ label, at, description, observationId }) {
    return (
      <div
        className={css({
          display: "grid",
          gridTemplateColumns: "24px 1fr",
          gap: 10,
          alignItems: "start",
          minWidth: 0,
        })}
      >
        <div
          className={css({
            width: 16,
            height: 16,
            borderRadius: 8,
            border: `2px solid ${at ? "#4CD964" : "#d2d6da"}`,
            marginTop: 2,
            background: at ? "#4CD964" : "#FFFFFF",
          })}
        />
        <div>
          <strong>{label}</strong>
          <div>
            {fmtDate(at, installation.timeZoneName, "EEE, d MMM yyyy HH:mm ZZZZ")}
          </div>
          <small style={{ color: gray40 }}>{description}</small>
        </div>
      </div>
    );
  }

  function HistoryTabs() {
    const events = booking?.events || [];
    const parkingSessions = booking?.parkingSessions || [];

    return (
      <RootOnly>
        <H3>History</H3>
        <div className={css({ display: "flex", gap: 8, marginBottom: 16 })}>
          <button
            type="button"
            onClick={() => setHistoryTab("parkingSessions")}
            className={css(tabStyle(historyTab === "parkingSessions"))}
          >
            Parking sessions ({parkingSessions.length})
          </button>
          <button
            type="button"
            onClick={() => setHistoryTab("events")}
            className={css(tabStyle(historyTab === "events"))}
          >
            Events ({events.length})
          </button>
        </div>

        {historyTab === "events" ? (
          events.length ? (
            <Table spaceBelow="medium">
              <TBody>
                {events.map((event, i) => (
                  <EventRow key={i} {...event} />
                ))}
              </TBody>
            </Table>
          ) : (
            <p className={css({ color: gray40 })}>No events recorded.</p>
          )
        ) : parkingSessions.length ? (
          <div className={css({ marginBottom: 32 })}>
            {parkingSessions.map((session) => (
              <ParkingSessionCard key={session.id} session={session} />
            ))}
          </div>
        ) : (
          <p className={css({ color: gray40 })}>No parking sessions recorded.</p>
        )}
      </RootOnly>
    );
  }

  const [open, setOpen] = React.useState(false);
  const history = useHistory();

  const deleteBooking = async (bookingId) => {
    setOpen(false);
    try {
      await drivoApollo.mutate({
        mutation: deleteBookingMutation,
        variables: {
          bookingId: bookingId,
        },
      });
      history.goBack();
    } catch (e) {
      console.error(e);
    }
  };

  const [booking, setBooking] = useState(null);
  const [bookingDetails, setBookingDetails] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [selectedHistoryTab, setHistoryTab] = useState(null);
  const historyTab =
    selectedHistoryTab ??
    (isParklioBayAccessEnabled(facility) ? "events" : "parkingSessions");

  const { bookingId } = match.params;
  const isRootAdmin = authManager.isRootAdmin();
  const { data, loading, error, refetch, stopPolling } = useQuery(bookingById, {
    fetchPolicy: "no-cache",
    variables: { bookingId, includeParkingSessions: isRootAdmin, includeVipAlertLogs: false },
    // Poll so the PIN section updates live as provisioning resolves
    // (pending → provisioned) without forcing a manual refresh.
    pollInterval: 5000,
  });

  useEffect(() => {
    if (!data) return;

    setBooking(data.booking);

    // Only stop polling on `revoked` — that's the truly terminal state.
    // `provisioned` mappings can still change when the booking is edited
    // (new dates / new rateCard / new guestName → validity + name update).
    // `failed` keeps polling because the worker may still be retrying.
    const status = data.booking?.pinpadAccess?.status;
    if (status === 'revoked') {
      stopPolling();
    }
  }, [data, stopPolling]);

  useEffect(() => {
    if (!booking) return;

    const fetchData = async () => {
      try {
        const res = await details(booking);
        setBookingDetails(res);
      } catch (e) {
        console.error(e.message);
      }
    };

    const fetchPaymentMethod = async () => {
      if (!booking?.stripeCustomerId) return;

      if (booking.stripeCustomerId) {
        try {
          const paymentMethod = await drivoApollo.query({
            query: getStripePaymentMethods,
            variables: {
              customerId: booking.stripeCustomerId,
              installationId: installation.id,
            },
          });
          setPaymentMethod(paymentMethod.data.getStripePaymentMethods[0]);
        } catch (error) {
          console.error("Error fetching payment method:", error);
        }
      }
    };

    fetchData();
    fetchPaymentMethod();
    /**
     * Narrowed deps: only refetch the rate-calc / payment-method when the
     * identifiers actually change. The booking object is replaced every
     * poll tick (5s) by setBooking(data.booking); depending on the whole
     * object would fire /bookings/details and getStripePaymentMethods every
     * 5s while the page is open. The PIN section still updates live via
     * setBooking (cheap render), but these expensive fetches don't re-run.
     */
  }, [booking?.id, booking?.stripeCustomerId]);

  if (loading)
    return (
      <CardContainer>
        <Loading />
      </CardContainer>
    );
  if (error)
    return (
      <CardContainer>
        <LoadingError error={error} />
      </CardContainer>
    );

  const { timeZoneName: tz, currency } = installation;
  const hasDetails =
    booking?.guestName ||
    booking?.guestEmail ||
    booking?.guestPhone ||
    booking?.guestRoom ||
    booking?.guestApartmentNumber ||
    booking?.guestReservation ||
    booking?.guestEmployeeCode ||
    booking?.guestPosition;
  const hasCharged = booking?.charges.length > 0;
  const isHourly = booking?.rateCard.type === "HOURLY";
  const isTieredHours = booking?.rateCard.type === "TIERED_HOURS";
  /**
   * FIXED_SESSION on an inactive day routes through hourly pricing — no billing.scheduled.
   * Also covers any card type that returns hourly-shaped billing data.
   */
  const hasScheduledBilling = !!bookingDetails?.billing?.scheduled?.fee;
  const isHourlyBased =
    isHourly || isTieredHours || (!!bookingDetails && !hasScheduledBilling); // Hour-based display (not day-based)

  function onRefund() {
    refetch();
  }

  const onCancellationRequest = () => {
    refetch();
  };

  const isCancellationProcessed =
    booking?.cancellationRequests?.[0]?.isProcessed || false;
  const isCancellationAlreadyRequested =
    booking?.cancellationRequests?.length > 0;
  const tabStyle = (active) => ({
    border: `1px solid ${active ? "#3127B8" : "#d2d6da"}`,
    borderRadius: 6,
    background: active ? "#3127B8" : "#FFFFFF",
    color: active ? "#FFFFFF" : "#333",
    cursor: "pointer",
    fontWeight: "bold",
    padding: "8px 14px",
  });

  return (
    <CardContainer>
      <div>
        <Container size="large" spaceBelow="medium">
          <BookingStatus status={booking?.status} />
          <H1 spaceBelow="none">{booking?.plate}</H1>
          <Byline>Booking Id: #{bookingId}</Byline>
          <Byline>
            Created At:{" "}
            {fmtDate(
              booking?.createdAt,
              installation.timeZoneName,
              "d MMM yyyy HH:mm ZZZZ",
            )}
          </Byline>
          <HR />
          <Table spaceBelow="medium">
            <TBody>
              <TR>
                <RowHead>Guest card</RowHead>
                <TD>
                  <RateCardLabel rateCard={booking?.rateCard} />
                </TD>
              </TR>
              <TR>
                <RowHead>VIP booking</RowHead>
                <TD>{booking?.isVip ? "Yes" : "No"}</TD>
              </TR>
              {booking?.staffName && (
                <TR>
                  <RowHead>Staff Name</RowHead>
                  <TD>{booking?.staffName}</TD>
                </TR>
              )}
              {booking?.reason &&
                booking?.rateCard?.name
                  ?.toLowerCase()
                  .match("free of charge|free|foc") != null && (
                  <TR>
                    <RowHead>Booking Reason</RowHead>
                    <TD>
                      {getBookingFocReasonLabel(booking.reason, installation.id) || "No reason"}
                    </TD>
                  </TR>
                )}
            </TBody>
          </Table>
        </Container>
        <Container size="large" spaceBelow="large">
          {hasDetails ? <H3>Guest details</H3> : null}
          {hasDetails ? (
            <Table spaceBelow="medium">
              <TBody>
                {booking?.guestName ? (
                  <TR>
                    <RowHead>Name</RowHead>
                    <TD>{booking?.guestName}</TD>
                  </TR>
                ) : null}
                {booking?.guestEmail ? (
                  <TR>
                    <RowHead>Email</RowHead>
                    <TD>{booking?.guestEmail}</TD>
                  </TR>
                ) : null}
                {booking?.guestCompany ? (
                  <TR>
                    <RowHead>Company</RowHead>
                    <TD>{booking?.guestCompany}</TD>
                  </TR>
                ) : null}
                {booking?.guestDepartment ? (
                  <TR>
                    <RowHead>Department</RowHead>
                    <TD>{booking?.guestDepartment}</TD>
                  </TR>
                ) : null}
                {booking?.guestPhone ? (
                  <TR>
                    <RowHead>Phone number</RowHead>
                    <TD>{booking?.guestPhone}</TD>
                  </TR>
                ) : null}
                {booking?.guestRoom ? (
                  <TR>
                    <RowHead>Room</RowHead>
                    <TD>{booking?.guestRoom}</TD>
                  </TR>
                ) : null}
                {booking?.guestApartmentNumber ? (
                  <TR>
                    <RowHead>Resident Apartment Number</RowHead>
                    <TD>{booking?.guestApartmentNumber}</TD>
                  </TR>
                ) : null}
                {booking?.guestReservation ? (
                  <TR>
                    <RowHead>Reservation</RowHead>
                    <TD>{booking?.guestReservation}</TD>
                  </TR>
                ) : null}
                {booking?.guestEmployeeCode ? (
                  <TR>
                    <RowHead>Employee Code</RowHead>
                    <TD>{booking?.guestEmployeeCode}</TD>
                  </TR>
                ) : null}
                {booking?.guestPosition ? (
                  <TR>
                    <RowHead>Position</RowHead>
                    <TD>{booking?.guestPosition}</TD>
                  </TR>
                ) : null}
                {getOhipConfirmation(booking?.pmsInformation) && (
                  <TR>
                    <RowHead>OHIP Confirmation Number</RowHead>
                    <TD>{getOhipConfirmation(booking.pmsInformation)}</TD>
                  </TR>
                )}
                {booking?.carType ? (
                  <TR>
                    <RowHead>Car Type</RowHead>
                    <TD>{booking?.carType}</TD>
                  </TR>
                ) : null}
                {booking?.carNotes ? (
                  <TR>
                    <RowHead>Car Notes</RowHead>
                    <TD>{booking?.carNotes}</TD>
                  </TR>
                ) : null}
              </TBody>
            </Table>
          ) : null}

          {booking?.pinpadAccess ? (
            <>
              <H3>Parking PIN</H3>
              <Table spaceBelow="medium">
                <TBody>
                  <TR>
                    <RowHead>Status</RowHead>
                    <TD>{booking.pinpadAccess.status}</TD>
                  </TR>
                  {booking.pinpadAccess.pin ? (
                    <TR>
                      <RowHead>PIN</RowHead>
                      <TD style={{ fontSize: 20, letterSpacing: 4, fontWeight: 'bold' }}>
                        {booking.pinpadAccess.pin}
                      </TD>
                    </TR>
                  ) : booking.pinpadAccess.status === 'pending' ? (
                    <TR>
                      <RowHead>PIN</RowHead>
                      <TD>Generating…</TD>
                    </TR>
                  ) : null}
                  {booking.pinpadAccess.validFrom ? (
                    <TR>
                      <RowHead>Valid from</RowHead>
                      <TD>
                        {fmtDate(
                          booking.pinpadAccess.validFrom,
                          installation.timeZoneName,
                          "EEE, d MMM yyyy HH:mm",
                        )}
                      </TD>
                    </TR>
                  ) : null}
                  {booking.pinpadAccess.validTo ? (
                    <TR>
                      <RowHead>Valid until</RowHead>
                      <TD>
                        {fmtDate(
                          booking.pinpadAccess.validTo,
                          installation.timeZoneName,
                          "EEE, d MMM yyyy HH:mm",
                        )}
                      </TD>
                    </TR>
                  ) : null}
                  {booking.pinpadAccess.status === 'failed' && booking.pinpadAccess.lastError ? (
                    <TR>
                      <RowHead>Last error</RowHead>
                      <TD style={{ color: '#c00' }}>{booking.pinpadAccess.lastError}</TD>
                    </TR>
                  ) : null}
                </TBody>
              </Table>
            </>
          ) : null}

          {isParklioBayAccessEnabled(facility) ? (
            <BayAccessSection bookingId={bookingId} />
          ) : null}

          <H3>Booking Period</H3>
          <Table spaceBelow="medium">
            <TBody>
              {isHourlyBased ? null : (
                <>
                  <TR>
                    <RowHead>Booking started at</RowHead>
                    <TD>
                      {fmtDate(
                        bookingDetails?.startedAt,
                        installation.timeZoneName,
                        "HH:mm ZZZZ",
                      )}
                    </TD>
                    <TD>
                      {fmtDate(
                        bookingDetails?.startedAt,
                        installation.timeZoneName,
                        "EEE, d MMM yyyy",
                      )}
                    </TD>
                  </TR>
                  <TR>
                    <RowHead>Booking start</RowHead>
                    <TD>
                      {fmtDate(
                        bookingDetails?.scheduled.startAt,
                        installation.timeZoneName,
                        "HH:mm ZZZZ",
                      )}
                    </TD>
                    <TD>
                      {fmtDate(
                        bookingDetails?.scheduled.startAt,
                        installation.timeZoneName,
                        "EEE, d MMM yyyy",
                      )}
                    </TD>
                  </TR>
                  <TR>
                    <RowHead>Booking end</RowHead>
                    <TD>
                      {fmtDate(
                        bookingDetails?.scheduled.endAt,
                        installation.timeZoneName,
                        "HH:mm ZZZZ",
                      )}
                    </TD>
                    <TD>
                      {fmtDate(
                        bookingDetails?.scheduled.endAt,
                        installation.timeZoneName,
                        "EEE, d MMM yyyy",
                      )}
                    </TD>
                  </TR>
                </>
              )}
              <TR>
                <RowHead>First Entry</RowHead>
                <TD>
                  {fmtDate(
                    bookingDetails?.enteredAt,
                    installation.timeZoneName,
                    "HH:mm ZZZZ",
                  )}
                </TD>
                <TD>
                  {fmtDate(
                    bookingDetails?.enteredAt,
                    installation.timeZoneName,
                    "EEE, d MMM yyyy",
                  )}
                </TD>
              </TR>
              <TR>
                <RowHead>Last Exit</RowHead>
                <TD>
                  {bookingDetails?.expired
                    ? "Unknown (vehicle missing)"
                    : fmtDate(
                        bookingDetails?.exitedAt,
                        installation.timeZoneName,
                        "HH:mm ZZZZ",
                      )}
                </TD>
                <TD>
                  {bookingDetails?.expired
                    ? "Unknown (vehicle missing)"
                    : fmtDate(
                        bookingDetails?.exitedAt,
                        installation.timeZoneName,
                        "EEE, d MMM yyyy",
                      )}
                </TD>
              </TR>
              {bookingDetails?.closedAt ? (
                <TR>
                  <RowHead>Closed</RowHead>
                  <TD>
                    {fmtDate(
                      bookingDetails?.closedAt,
                      installation.timeZoneName,
                      "HH:mm ZZZZ",
                    )}
                  </TD>
                  <TD>
                    {fmtDate(
                      bookingDetails?.closedAt,
                      installation.timeZoneName,
                      "EEE, d MMM yyyy",
                    )}
                  </TD>
                </TR>
              ) : null}
            </TBody>
          </Table>

          <RootOnly>
            <HistoryTabs />
          </RootOnly>

          {booking?.emails.length ? (
            <PermissionOnly permissions={PERMISSIONS.BOOKINGS_MANAGE}>
              <H4>Email History</H4>
              <Table spaceBelow="medium">
                <TBody>
                  {booking?.emails.map((x, i) => (
                    <EmailRow appConfig={appConfig} {...x} key={i} />
                  ))}
                </TBody>
              </Table>
            </PermissionOnly>
          ) : null}

          <H3>Parking fee</H3>
          <Table spaceBelow="medium">
            {isHourlyBased ? (
              <>
                <TBody>
                  <TR>
                    <RowHead>
                      {bookingDetails?.closedAt ? "Final Fee" : "Current Fee"}
                    </RowHead>
                    <TD>
                      {bookingDetails?.enteredAt
                        ? `${bookingDetails?.billing.hours.total} hours`
                        : "No entry"}
                    </TD>
                    <TD>
                      {formatCurrency(
                        bookingDetails?.billing.fee.total,
                        currency,
                      )}
                      {bookingDetails?.billing.capped ? (
                        <>
                          {" "}
                          <CappedPill />{" "}
                        </>
                      ) : null}
                      {bookingDetails?.expired ? (
                        <>
                          {" "}
                          <ExpiryPill />{" "}
                        </>
                      ) : null}
                    </TD>
                  </TR>
                  {isTieredHours && bookingDetails?.overstayed ? (
                    <TR>
                      <RowHead>Overstay</RowHead>
                      <TD>{bookingDetails?.billing.overstay.hours} hours</TD>
                      <TD>
                        {formatCurrency(
                          bookingDetails?.billing.overstay.fee.total,
                          currency,
                        )}
                      </TD>
                    </TR>
                  ) : null}
                </TBody>
              </>
            ) : (
              <>
                <TBody>
                  <TR>
                    <RowHead>Booked</RowHead>
                    <TD>
                      {format24HPeriod(
                        bookingDetails?.scheduled.days,
                        booking?.rateCard,
                      )}
                    </TD>
                    <TD>
                      {formatCurrency(
                        bookingDetails?.billing.scheduled.fee.total,
                        currency,
                      )}
                      {bookingDetails?.expired ? (
                        <>
                          {" "}
                          <ExpiryPill />{" "}
                        </>
                      ) : null}
                    </TD>
                  </TR>
                  {bookingDetails?.overstayed ? (
                    <TR>
                      <RowHead>Overstay</RowHead>
                      <TD>
                        {bookingDetails?.billing.overstay.hours.total.toFixed(
                          2,
                        )}{" "}
                        hours
                        {bookingDetails?.billing.overstay.capped ? (
                          <>
                            {" "}
                            <CappedPill />{" "}
                          </>
                        ) : null}
                      </TD>
                      <TD>
                        {formatCurrency(
                          bookingDetails?.billing.overstay.fee.total,
                          currency,
                        )}
                      </TD>
                    </TR>
                  ) : null}
                </TBody>
              </>
            )}

            {booking?.charges.length ? (
              <TBody>
                {booking?.charges.map((x, i) => (
                  <ChargeRow {...x} key={i} currency={currency}>
                    Charge
                  </ChargeRow>
                ))}
              </TBody>
            ) : null}
            {booking?.refunds.length ? (
              <TBody>
                {booking?.refunds.map((x, i) => (
                  <RefundRow
                    appConfig={appConfig}
                    {...x}
                    currency={currency}
                    key={i}
                  />
                ))}
              </TBody>
            ) : null}
          </Table>

          {booking?.charges.length ? (
            <PermissionOnly permissions={PERMISSIONS.BOOKINGS_MANAGE}>
              <RequestRefund booking={booking} onRefund={onRefund} />
            </PermissionOnly>
          ) : null}

          {booking?.bookingExtras && booking?.bookingExtras.length > 0 && (
            <>
              <H3>Extras</H3>
              <Table spaceBelow="medium">
                <TBody>
                  {(() => {
                    /*
                     * Group booking extras by rateCardExtra id so the same
                     * extra added multiple times shows as a single row with
                     * "count x unit" instead of N identical rows. The grand
                     * total is the sum of every line (unit price * count).
                     */
                    const grouped = booking.bookingExtras.reduce((acc, be) => {
                      const id = be.rateCardExtra.id;
                      if (!acc[id]) {
                        acc[id] = { extra: be.rateCardExtra, count: 0 };
                      }
                      acc[id].count += 1;
                      return acc;
                    }, {});
                    const groupedArray = Object.values(grouped);
                    const grandTotal = groupedArray.reduce(
                      (sum, g) => sum + Number(g.extra.amount) * g.count,
                      0,
                    );
                    return (
                      <>
                        {groupedArray.map(({ extra, count }) => {
                          const unit = Number(extra.amount);
                          const lineTotal = unit * count;
                          return (
                            <TR key={extra.id}>
                              <RowHead>{extra.description}</RowHead>
                              <TD>{count} × {formatCurrency(unit, currency)}</TD>
                              <TD>{formatCurrency(lineTotal, currency)}</TD>
                            </TR>
                          );
                        })}
                        <TR>
                          <RowHead><strong>Total</strong></RowHead>
                          <TD></TD>
                          <TD><strong>{formatCurrency(grandTotal, currency)}</strong></TD>
                        </TR>
                      </>
                    );
                  })()}
                </TBody>
              </Table>
            </>
          )}

          {(booking?.stripeSource || paymentMethod) && (
            <div>
              <H3>Payment details</H3>
              <StripeElementsInput
                appConfig={appConfig}
                currency={installation.currency}
                country={facility?.city?.country}
                installationId={installation.id}
                guestEmail={booking?.guestEmail}
                paymentMethod={paymentMethod}
                setStripeCustomerId={() => {}}
                value={booking?.stripeSource}
              />
            </div>
          )}
        </Container>

        {installation.id === "f4fb122c-cc71-430a-9fdb-2e5a7c2f6fa4" && (
          <PermissionOnly permissions={PERMISSIONS.BOOKINGS_MANAGE}>
            <RequestCancellation
              key={booking?.id}
              booking={booking}
              onCancellationRequest={onCancellationRequest}
              isCancellationAlreadyRequested={isCancellationAlreadyRequested}
              isCancellationProcessed={isCancellationProcessed}
            />
          </PermissionOnly>
        )}

        <PermissionOnly permissions={PERMISSIONS.BOOKINGS_MANAGE}>
          <ColContainer spaceBelow="medium">
            {!isLocked(booking) && (
              <Button
                block
                flavour="primary"
                to={{
                  pathname: match.url.replace(bookingId, "create"),
                  search: `?bookingId=${bookingId}`,
                }}
              >
                Edit this booking
              </Button>
            )}
            <ResendEmail booking={booking} hasCharged={hasCharged} />
          </ColContainer>
          {booking?.status === "PENDING_ENTRY" && (
            <Button
              block
              flavour="danger"
              onClick={() => {
                setOpen(true);
              }}
            >
              Delete this booking
            </Button>
          )}
        </PermissionOnly>
      </div>

      {/*    dialog for delete confirmation */}
      <Dialog
        open={open}
        onClose={() => {
          setOpen(false);
        }}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to delete this booking ?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <HollowButton
            size={"small"}
            onClick={() => {
              setOpen(false);
            }}
          >
            Cancel
          </HollowButton>
          <HollowButton
            size={"small"}
            flavour="danger"
            onClick={() => {
              deleteBooking(bookingId);
            }}
            autoFocus
          >
            Delete
          </HollowButton>
        </DialogActions>
      </Dialog>
    </CardContainer>
  );
}

export default withInstallation(withAppConfig(BookingDetail));
